import React, { FC } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';

type PointsRaceTableProps = {
    values: string[][];
    title?: string;
};

export const PointsRaceTable: FC<PointsRaceTableProps> = ({ values, title }) => {
    const [headers, ...rows] = values;

    const countryColIndex = headers.findIndex((h) => h.toLowerCase() === 'country');
    const qColIndex = headers.findIndex((h) => h.toLowerCase() === 'q');

    const validQValues = ['Q', 'QG', 'QB', 'Q5'];
    const isQualified = (value?: string) => value ? validQValues.includes(value.toUpperCase().trim()) : false;
    const isWinner = (value?: string) => value?.toLowerCase() === 'winner';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // =========================================================
    // 📱 MOBILNÍ ZOBRAZENÍ (PRO BODOVACÍ ZÁVOD JEDNOTLIVCŮ)
    // =========================================================
    if (isMobile) {
        const headers = values[0];
        const rows = values.slice(1);

        const colIndex = (name: string) => headers.findIndex((h) => h.toLowerCase().includes(name.toLowerCase()));

        const rankCol = colIndex('rnk');
        const bibCol = colIndex('bib');
        const firstNameCol = colIndex('first');
        const lastNameCol = colIndex('last');
        const countryCol = colIndex('country');
        const pointsCol = colIndex('points') !== -1 ? colIndex('points') : colIndex('total');
        const qCol = colIndex('q');

        // 🎯 DYNAMICKÝ SPRINT: Najde sloupec "Sprint X/Y"
        const sprintCol = headers.findIndex((h) => h && h.toLowerCase().includes('sprint') && h.includes('/'));

        return (
            <Box sx={{ py: 2 }}>
                {title && (
                    <Paper sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
                        <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.900', color: 'common.white' }}>
                            <Typography fontWeight={600}>{title}</Typography>
                        </Box>
                    </Paper>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2, marginBottom: 2 }}>
                    {rows.map((row, index) => {
                        const rank = row[rankCol];
                        const bib = row[bibCol];
                        const firstName = row[firstNameCol];
                        const lastName = row[lastNameCol];
                        const country = row[countryCol];
                        const value = row[pointsCol] || '';
                        const q = row[qCol];
                        const sprintValue = sprintCol !== -1 ? row[sprintCol] : '';

                        return (
                            <Paper key={index} sx={{ p: 2, borderRadius: 3, display: 'flex' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1.5 }}>
                                    {/* LEVÁ ČÁST (Pořadí + Jméno + Vlajka) */}
                                    <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                                        <Typography fontWeight={700} sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                                            {rank === 'DNF' || rank === 'ABD' ? '' : '# '}
                                            {rank}
                                            <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.1 }}>{bib}</Typography>
                                        </Typography>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1, p: 0.5, borderRadius: 2 }}>
                                            <Box>
                                                <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>{lastName}</Typography>
                                                <Typography sx={{ fontSize: '0.75rem', pl: 1.5, color: 'text.secondary', lineHeight: 1.1 }}>{firstName}</Typography>
                                            </Box>
                                            <img src={getFlagUrl(country)} alt={country} className="flag-icon" style={{ width: '24px', height: '17px' }} />
                                        </Box>
                                    </Box>

                                    {/* 🎯 STŘEDNÍ ČÁST (Sprint X/Y kartička) */}
                                    {sprintCol !== -1 && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                px: 1,
                                                py: 0.5,
                                                mx: 0.5,
                                                borderRadius: 1.5,
                                                backgroundColor: sprintValue && sprintValue !== '0' ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
                                                border: sprintValue && sprintValue !== '0' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px dashed rgba(150, 150, 150, 0.2)',
                                                minWidth: 68,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 700, lineHeight: 1.1, mb: 0.2, whiteSpace: 'nowrap' }}>
                                                {headers[sprintCol]}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: sprintValue && sprintValue !== '0' ? 'primary.main' : 'text.disabled', lineHeight: 1.1 }}>
                                                {sprintValue || '—'}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* PRAVÁ ČÁST (Celkové body) */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: row[pointsCol] ? 45 : 80, justifyContent: 'end' }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{value}</Typography>
                                        {isQualified(q) && <span className="qualified-badge">{q.toUpperCase().trim()}</span>}
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            </Box>
        );
    }

    // =========================================================
    // 💻 DESKTOP ZOBRAZENÍ
    // =========================================================
    const sprintColIdx = headers.findIndex((h) => h && h.toLowerCase().includes('sprint') && h.includes('/'));

    return (
        <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: 2, overflow: 'hidden', mt: 3 }}>
            {title && (
                <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.900', color: 'common.white', display: 'flex', justifyContent: 'center' }}>
                    <Typography fontWeight={600}>{title}</Typography>
                </Box>
            )}
            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {headers.map((header, index) => (
                                <TableCell key={index} align="center" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', backgroundColor: 'grey.900', color: 'common.white', whiteSpace: 'nowrap' }}>
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, rowIndex) => (
                            <TableRow key={rowIndex} hover>
                                {headers.map((_, cellIndex) => {
                                    const cellValue = row[cellIndex];
                                    const winner = isWinner(cellValue);
                                    const qualified = cellIndex === qColIndex && isQualified(cellValue);
                                    const isLiveSprintCol = cellIndex === sprintColIdx;

                                    if (qualified) {
                                        return <TableCell key={cellIndex} align="center"><span className="qualified-badge">{cellValue.toUpperCase().trim()}</span></TableCell>;
                                    }

                                    if (cellIndex === countryColIndex && cellValue) {
                                        return (
                                            <TableCell key={cellIndex} align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <img src={getFlagUrl(cellValue)} alt={cellValue} className="flag-icon" />
                                                </Box>
                                            </TableCell>
                                        );
                                    }

                                    return (
                                        <TableCell
                                            key={cellIndex}
                                            align="center"
                                            sx={{
                                                color: isLiveSprintCol && cellValue ? 'primary.main' : winner ? 'success.main' : 'text.primary',
                                                fontWeight: isLiveSprintCol && cellValue ? 700 : winner || cellIndex === 0 ? 600 : 400,
                                                backgroundColor: isLiveSprintCol ? 'rgba(59, 130, 246, 0.05)' : 'inherit',
                                            }}
                                        >
                                            {cellValue ?? '—'}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};